import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
    Package,
    PackageCheck,
    AlertTriangle,
    Plus,
    Eye,
    UserPlus01,
    Users01,
    Globe02
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { containerService } from '@/data/mock-containers';
import { collaborationService } from '@/data/mock-collaborations';
import { teamService } from '@/data/mock-team';
import type { Container, ContainerStats, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import WorldRoutesMapPreview from '@/components/application/map/world-routes-map-preview';

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    trend?: { value: number; isPositive: boolean };
    vsLastMonthText?: string;
}

const StatCard = ({ title, value, icon: Icon, color, trend, vsLastMonthText }: StatCardProps) => {
    const colorClasses = {
        brand: 'text-brand-600 bg-brand-50',
        success: 'text-success-600 bg-success-50',
        warning: 'text-warning-600 bg-warning-50',
        error: 'text-error-600 bg-error-50'
    };

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <div className="flex items-center">
                <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
                    <Icon className="size-6" />
                </div>
                <div className="ml-4">
                    <p className="text-sm font-medium text-tertiary">{title}</p>
                    <p className="text-2xl font-semibold text-primary">{value}</p>
                    {trend && vsLastMonthText && (
                        <p className={`text-sm ${trend.isPositive ? 'text-success-600' : 'text-error-600'}`}>
                            {trend.isPositive ? '+' : ''}{trend.value}% {vsLastMonthText}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ShipperDashboardPage = () => {
    const { t } = useTranslation(['pages:dashboard', 'pages:containers', 'pages:receivers', 'common', 'types']);
    const { translateDocumentStatus, translateRiskLevel, translateContainerStatus } = useI18n();
    const [stats, setStats] = useState<ContainerStats>({
        total: 0,
        inTransit: 0,
        delivered: 0,
        delayed: 0,
        pendingDocuments: 0,
        highRisk: 0,
    });
    const [recentContainers, setRecentContainers] = useState<Container[]>([]);
    const [collaborationStats, setCollaborationStats] = useState({ total: 0, active: 0, pending: 0 });
    const [teamStats, setTeamStats] = useState({ total: 0, active: 0 });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = localStorage.getItem('accountId') || '1';

            // Load container stats
            const containerStats = containerService.getStats(accountId);
            setStats(containerStats);

            // Load recent containers
            const recent = containerService.getRecent(5, accountId);
            setRecentContainers(recent);

            // Load collaboration stats
            const collabStats = collaborationService.getStats(accountId);
            setCollaborationStats(collabStats);

            // Load team stats
            const teamStatsData = teamService.getStats(accountId);
            setTeamStats(teamStatsData);

            setIsLoading(false);
        };
        loadData();
    }, []);

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    // Helper per convertire color names a hex
    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef'; // brand-600
            case 'success': return '#079485'; // success-600
            case 'warning': return '#dc6803'; // warning-600
            case 'error': return '#d92d20'; // error-600
            default: return '#6b7280'; // gray-500
        }
    };

    // Calcola distribuzioni per i grafici
    const distributionData = useMemo(() => {
        const accountId = localStorage.getItem('accountId') || '1';
        const userContainers = containerService.getAll().filter(c => c.shipperId === accountId);
        const total = userContainers.length;

        // Risk Levels
        const riskDistribution = userContainers.reduce((acc, c) => {
            acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
            return acc;
        }, {} as Record<RiskLevel, number>);

        const riskData = [
            { label: translateRiskLevel('Low'), value: riskDistribution.Low || 0, color: getColorHex('success') },
            { label: translateRiskLevel('Medium'), value: riskDistribution.Medium || 0, color: getColorHex('warning') },
            { label: translateRiskLevel('High'), value: riskDistribution.High || 0, color: getColorHex('error') },
            { label: translateRiskLevel('Critical'), value: riskDistribution.Critical || 0, color: getColorHex('error') },
        ];

        // Container Status
        const statusDistribution = userContainers.reduce((acc, c) => {
            acc[c.status] = (acc[c.status] || 0) + 1;
            return acc;
        }, {} as Record<ContainerStatus, number>);

        const statusData = [
            { label: translateContainerStatus('In Transit'), value: statusDistribution['In Transit'] || 0, color: getColorHex('brand') },
            { label: translateContainerStatus('Delivered'), value: statusDistribution.Delivered || 0, color: getColorHex('success') },
            { label: translateContainerStatus('Delayed'), value: statusDistribution.Delayed || 0, color: getColorHex('error') },
            { label: translateContainerStatus('At Port'), value: statusDistribution['At Port'] || 0, color: getColorHex('warning') },
        ];

        // Document Status
        const documentDistribution = userContainers.reduce((acc, c) => {
            acc[c.documentStatus] = (acc[c.documentStatus] || 0) + 1;
            return acc;
        }, {} as Record<DocumentStatus, number>);

        const documentData = [
            { label: translateDocumentStatus('Completo'), value: documentDistribution.Completo || 0, color: getColorHex('success') },
            { label: translateDocumentStatus('Pending'), value: documentDistribution.Pending || 0, color: getColorHex('warning') },
            { label: translateDocumentStatus('Error'), value: documentDistribution.Error || 0, color: getColorHex('error') },
        ];

        return { riskData, statusData, documentData, total };
    }, [stats.total, translateRiskLevel, translateContainerStatus, translateDocumentStatus]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('shipper.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('shipper.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('shipper.subtitle')}
                    </p>
                </div>
                <div className="flex gap-3">
                    <Link
                        to="/shipper/containers/new"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    >
                        <Plus className="size-5" />
                        {t('list.newContainer', { ns: 'pages:containers' })}
                    </Link>
                    <Link
                        to="/shipper/receivers"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-md font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <UserPlus01 className="size-5" />
                        {t('list.invite', { ns: 'pages:receivers' })}
                    </Link>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title={t('shipper.stats.totalContainers')}
                    value={stats.total}
                    icon={Package}
                    color="brand"
                    trend={{ value: 12, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('shipper.stats.inTransit')}
                    value={stats.inTransit}
                    icon={PackageCheck}
                    color="success"
                    trend={{ value: 8, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('shipper.stats.delivered')}
                    value={stats.delivered}
                    icon={PackageCheck}
                    color="success"
                    trend={{ value: 15, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('shipper.stats.pendingDocuments')}
                    value={stats.pendingDocuments}
                    icon={AlertTriangle}
                    color="warning"
                    trend={{ value: 3, isPositive: false }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
            </div>

            {/* Additional Stats Row */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <StatCard
                    title={t('shipper.stats.highRisk')}
                    value={stats.highRisk}
                    icon={AlertTriangle}
                    color="error"
                />
                <StatCard
                    title={t('shipper.collaborations.active')}
                    value={collaborationStats.active}
                    icon={Users01}
                    color="brand"
                />
                <StatCard
                    title={t('shipper.team.active')}
                    value={teamStats.active}
                    icon={UserPlus01}
                    color="success"
                />
            </div>

            {/* Distribution Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <DistributionChart
                    title={t('list.charts.riskLevels', { ns: 'pages:containers' }) || 'Risk Levels Distribution'}
                    data={distributionData.riskData}
                    total={distributionData.total}
                />
                <DistributionChart
                    title={t('list.charts.containerStatus', { ns: 'pages:containers' }) || 'Container Status Distribution'}
                    data={distributionData.statusData}
                    total={distributionData.total}
                />
                <DistributionChart
                    title={t('list.charts.documentStatus', { ns: 'pages:containers' }) || 'Document Status Distribution'}
                    data={distributionData.documentData}
                    total={distributionData.total}
                />
            </div>

            {/* World Routes Map Preview */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-primary">{t('worldMap.title', { ns: 'pages:containers' })}</h3>
                        <p className="text-sm text-tertiary">{t('worldMap.updateInfo', { ns: 'pages:containers' })}</p>
                    </div>
                    <Link
                        to="/shipper/containers/world-map"
                        className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <Globe02 className="size-4" />
                        {t('list.viewMap', { ns: 'pages:containers' })}
                    </Link>
                </div>
                <WorldRoutesMapPreview height={420} />
            </div>

            {/* Recent Containers Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('shipper.recentContainers.title')}
                    description={t('shipper.recentContainers.description')}
                    contentTrailing={
                        <div className="flex items-center gap-2">
                            <Link
                                to="/shipper/containers"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                            >
                                <Eye className="size-4" />
                                {t('shipper.recentContainers.viewAll')}
                            </Link>
                            <Link
                                to="/shipper/containers/world-map"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                            >
                                <Globe02 className="size-4" />
                                {t('list.viewMap', { ns: 'pages:containers' })}
                            </Link>
                        </div>
                    }
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('shipper.recentContainers.headers.containerId')} isRowHeader />
                        <Table.Head label={t('shipper.recentContainers.headers.route')} />
                        <Table.Head label={t('shipper.recentContainers.headers.progress')} />
                        <Table.Head label={t('shipper.recentContainers.headers.eta')} />
                        <Table.Head label={t('shipper.recentContainers.headers.docStatus')} />
                        <Table.Head label={t('shipper.recentContainers.headers.riskLevel')} />
                        <Table.Head label={t('shipper.recentContainers.headers.value')} />
                    </Table.Header>
                    <Table.Body>
                        {recentContainers.map((container) => (
                            <Table.Row key={container.id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{container.containerNumber}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="text-sm text-tertiary">
                                        {container.route.from} → {container.route.to}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <ProgressBar
                                            value={container.progress}
                                            min={0}
                                            max={100}
                                        />
                                        <span className="text-sm text-tertiary">{container.progress}%</span>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {container.eta.toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                        {translateDocumentStatus(container.documentStatus)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                        {translateRiskLevel(container.riskLevel)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {container.value.currency} {container.value.amount.toLocaleString()}
                                    </span>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>
        </div>
    );
};
